import React, { useEffect, useState } from "react";
import api, { fmtFCFA } from "@/lib/api";
import { DashboardShell, KpiCard, StatusBadge } from "@/components/DashboardShell";
import { PropertyFormDialog } from "@/components/PropertyFormDialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Building2, RefreshCw, Percent } from "lucide-react";

export default function PartnerDashboard() {
  const [tab, setTab] = useState("apercu");
  const [stats, setStats] = useState(null);
  const [props, setProps] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, p, c] = await Promise.all([
        api.get("/partner/stats"), api.get("/partner/properties"), api.get("/partner/commissions"),
      ]);
      setStats(s.data); setProps(p.data); setCommissions(c.data);
    } catch {}
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => { if (!window.confirm("Supprimer ce bien ?")) return; await api.delete(`/properties/${id}`); toast.success("Bien supprimé"); load(); };

  const tabs = [
    { value: "apercu", label: "Aperçu" },
    { value: "biens", label: `Mes biens (${props.length})` },
    { value: "commissions", label: `Commissions (${commissions.length})` },
  ];
  const AddDialog = (
    <PropertyFormDialog onSaved={load} partnerMode trigger={<Button className="bg-gold text-navy hover:bg-gold-light font-semibold rounded-lg" data-testid="partner-add-btn"><Plus className="h-4 w-4 mr-1.5" /> Déposer un bien</Button>} />
  );
  const RefreshBtn = (
    <Button onClick={load} variant="outline" className="rounded-lg" data-testid="partner-refresh"><RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Actualiser</Button>
  );

  const due = stats?.commission_due || 0;
  const paid = stats?.commission_paid || 0;

  return (
    <DashboardShell title="Espace Partenaire" subtitle="Déposez vos biens, suivez leur performance et vos commissions avec IMMOMAXE." tabs={tabs} active={tab} onTab={setTab}>
      {tab === "apercu" && stats && (
        <>
          <div className="flex justify-end gap-2 mb-4">{RefreshBtn}{AddDialog}</div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <KpiCard label="Biens déposés" value={stats.properties} testid="pk-props" />
            <KpiCard label="Biens publiés" value={stats.published} accent testid="pk-published" />
            <KpiCard label="Vues cumulées" value={stats.views} testid="pk-views" />
            <KpiCard label="Demandes reçues" value={stats.requests} testid="pk-requests" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
            <div className="rounded-xl bg-navy text-white p-6" data-testid="pk-commission-due">
              <div className="flex items-center gap-2 text-white/60 text-sm"><Percent className="h-4 w-4 text-gold" /> Commissions dues</div>
              <div className="mt-2 font-mono text-3xl font-bold text-gold">{fmtFCFA(due)}</div>
              <p className="mt-1 text-xs text-white/50">Montant à régler à IMMOMAXE sur vos transactions conclues.</p>
            </div>
            <div className="rounded-xl bg-white border border-black/5 p-6" data-testid="pk-commission-paid">
              <div className="flex items-center gap-2 text-slate-500 text-sm"><Percent className="h-4 w-4 text-green-600" /> Commissions réglées</div>
              <div className="mt-2 font-mono text-3xl font-bold text-navy">{fmtFCFA(paid)}</div>
              <p className="mt-1 text-xs text-slate-400">Historique des commissions déjà payées.</p>
            </div>
          </div>
          <div className="rounded-xl bg-white border border-black/5 p-6">
            <h3 className="font-serif text-xl font-semibold text-navy mb-4">Mes biens déposés</h3>
            {props.length === 0 ? <p className="text-slate-400 py-8 text-center">Aucun bien. Déposez votre premier bien.</p> : (
              <div className="space-y-3">
                {props.map((p) => (
                  <div key={p.id} className="flex items-center justify-between rounded-lg border p-4" data-testid={`partner-ledger-${p.reference}`}>
                    <div className="flex items-center gap-3">
                      <img src={p.images?.[0]} alt="" className="h-14 w-14 rounded-lg object-cover" />
                      <div><div className="font-medium text-navy">{p.title}</div><div className="text-xs text-slate-400">{p.reference} · {p.city} · {p.views || 0} vues</div></div>
                    </div>
                    <div className="flex items-center gap-3">
                      <StatusBadge status={p.status} />
                      <div className="text-right"><div className="font-mono font-bold text-navy">{fmtFCFA(p.price)}</div><div className="text-xs text-gold">{p.published ? "Publié" : "En attente"}</div></div>
                      <PropertyFormDialog property={p} onSaved={load} partnerMode trigger={<Button variant="ghost" size="icon" className="text-navy hover:bg-navy/5" data-testid={`partner-edit-${p.reference}`}><Pencil className="h-4 w-4" /></Button>} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {tab === "biens" && (
        <>
          <div className="flex justify-end gap-2 mb-4">{RefreshBtn}{AddDialog}</div>
          {props.length === 0 ? <div className="rounded-xl bg-white border border-black/5 py-16 text-center text-slate-400"><Building2 className="mx-auto h-10 w-10 mb-3 opacity-40" />Aucun bien déposé.</div> : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {props.map((p) => (
                <div key={p.id} className="rounded-xl bg-white border border-black/5 overflow-hidden" data-testid={`partner-prop-${p.reference}`}>
                  <div className="relative"><img src={p.images?.[0]} alt="" className="aspect-[4/3] w-full object-cover" /><div className="absolute top-2 left-2"><StatusBadge status={p.status} /></div></div>
                  <div className="p-5">
                    <div className="font-mono text-xs text-gold">{p.reference}</div>
                    <h4 className="mt-1 font-semibold text-navy">{p.title}</h4>
                    <div className="mt-2 flex items-center justify-between">
                      <span className="font-mono font-bold text-navy">{fmtFCFA(p.price)}</span>
                      <div className="flex gap-1">
                        <PropertyFormDialog property={p} onSaved={load} partnerMode trigger={<Button variant="ghost" size="icon" className="text-navy hover:bg-navy/5" data-testid={`partner-edit2-${p.reference}`}><Pencil className="h-4 w-4" /></Button>} />
                        <Button onClick={() => del(p.id)} variant="ghost" size="icon" className="text-red-600 hover:bg-red-50" data-testid={`partner-del-${p.reference}`}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {tab === "commissions" && (
        <>
          <div className="flex justify-end mb-4">{RefreshBtn}</div>
          <div className="rounded-xl bg-white border border-black/5 overflow-hidden" data-testid="partner-commissions">
            {commissions.length === 0 ? (
              <div className="py-16 text-center text-slate-400"><Percent className="mx-auto h-10 w-10 mb-3 opacity-40" />Aucune commission pour le moment. Elles apparaissent lorsqu'une transaction sur vos biens est conclue.</div>
            ) : (
              <table className="w-full text-sm min-w-[640px]">
                <thead className="bg-ivory text-left text-slate-500"><tr>
                  <th className="px-5 py-3 font-medium">Bien</th><th className="px-5 py-3 font-medium">Type</th>
                  <th className="px-5 py-3 font-medium">Base</th><th className="px-5 py-3 font-medium">Taux</th>
                  <th className="px-5 py-3 font-medium">Montant</th><th className="px-5 py-3 font-medium">Statut</th>
                </tr></thead>
                <tbody>
                  {commissions.map((c) => (
                    <tr key={c.id} className="border-t" data-testid={`partner-com-${c.id}`}>
                      <td className="px-5 py-3 font-mono text-xs text-gold">{c.property_ref}</td>
                      <td className="px-5 py-3 capitalize">{c.transaction}</td>
                      <td className="px-5 py-3 text-slate-500">{fmtFCFA(c.base_amount)}</td>
                      <td className="px-5 py-3">{c.rate}%</td>
                      <td className="px-5 py-3 font-mono font-bold text-navy">{fmtFCFA(c.amount)}</td>
                      <td className="px-5 py-3"><span className={`rounded-full px-2.5 py-1 text-xs font-medium ${c.status === "paid" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{c.status === "paid" ? "Réglée" : "Due"}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </DashboardShell>
  );
}
